import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { planService } from "../../services/PlanServices";
import { toast } from "react-toastify";

const PlansForm = () => {
  const [category, setCategory] = useState("");
  const [investmentMin, setInvestmentMin] = useState("");
  const [investmentMax, setInvestmentMax] = useState("");
  const [profitMin, setProfitMin] = useState("");
  const [profitMax, setProfitMax] = useState("");
  const [lockInPeriod, setLockInPeriod] = useState("");
  const [lockInPeriodUnit, setLockInPeriodUnit] = useState("days");
  const [planType, setPlanType] = useState("standard");
  const [status, setStatus] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchPlanDetails();
    }
  }, [id]);

  const fetchPlanDetails = async () => {
    try {
      const response = await planService.getPlanDetails(id);
      if (response.status === 200) {
        const plan = response.data;
        setCategory(plan.category);
        setInvestmentMin(plan.investmentAmount.min);
        setInvestmentMax(plan.investmentAmount.max ?? "");
        setProfitMin(plan.profitPercentage.min);
        setProfitMax(plan.profitPercentage.max);
        setLockInPeriod(plan.lockInPeriod);
        setLockInPeriodUnit(plan.lockInPeriodUnit);
        setPlanType(plan.planType);
        setStatus(plan.status);
      } else {
        toast.error(response.data?.message);
      }
    } catch (error) {
      toast.error("Failed to fetch plan details");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = {
      category,
      investmentAmount: {
        min: Number(investmentMin),
        max: investmentMax ? Number(investmentMax) : null,
      },
      profitPercentage: {
        min: Number(profitMin),
        max: Number(profitMax),
      },
      lockInPeriod: Number(lockInPeriod),
      lockInPeriodUnit,
      planType,
      status,
    };

    try {
      if (id) {
        const response = await planService.updatePlan(id, data);
        if (response.status === 200) {
          toast.success("Plan updated successfully");
        }
      } else {
        const response = await planService.addPlan(data);
        if (response.status === 200) {
          toast.success("Plan added successfully");
        }
      }
      navigate("/admin/plans");
    } catch (error) {
      toast.error("Failed to save plan");
    }
  };

  return (
    <>
      <div className="body-header border-bottom d-flex py-3">
        <div className="container-xxl">
          <div className="row align-items-center">
            <div className="col">
              <h1 className="h4 mt-1">{id ? "Edit Plan" : "Create New Plan"}</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="body d-flex py-3">
        <div className="container-xxl">
          <div className="row align-item-center">
            <div className="col-md-12">
              <div className="card mb-3">
                <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom-0">
                  <h6 className="mb-0 fw-bold">{id ? "Edit Plan" : "Create New Plan"}</h6>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Category</label>
                        <input
                          type="text"
                          className="form-control"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          required
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Investment Min</label>
                        <input
                          type="number"
                          className="form-control"
                          value={investmentMin}
                          onChange={(e) => setInvestmentMin(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Investment Max</label>
                        <input
                          type="number"
                          className="form-control"
                          value={investmentMax}
                          onChange={(e) => setInvestmentMax(e.target.value)}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Profit Min (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={profitMin}
                          onChange={(e) => setProfitMin(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Profit Max (%)</label>
                        <input
                          type="number"
                          className="form-control"
                          value={profitMax}
                          onChange={(e) => setProfitMax(e.target.value)}
                          required
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Lock-In Period</label>
                        <input
                          type="number"
                          className="form-control"
                          value={lockInPeriod}
                          onChange={(e) => setLockInPeriod(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Lock-In Unit</label>
                        <select
                          className="form-control"
                          value={lockInPeriodUnit}
                          onChange={(e) => setLockInPeriodUnit(e.target.value)}
                          required
                        >
                          <option value="days">Days</option>
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Plan Type</label>
                        <select
                          className="form-control"
                          value={planType}
                          onChange={(e) => setPlanType(e.target.value)}
                          required
                        >
                          <option value="standard">Standard</option>
                          <option value="vip">VIP</option>
                        </select>
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-control"
                          value={status}
                          onChange={(e) => setStatus(e.target.value === "true")}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary mt-4">
                      {id ? "Update" : "Create"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlansForm;
